import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	forwardRef,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgZone,
	OnInit,
	ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";

import { IMyDate, IMyDayLabels } from "@intocare/ui-my-date-picker";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DateInputCellComponent),
	multi: true,
};

@Component({
	selector: "ic-date-input-cell",
	styleUrls: ["date-cell.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./date-cell.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DateInputCellComponent implements ControlValueAccessor, OnInit {
	private translator = inject(TranslateService);
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);
	private ngZone = inject(NgZone);

	@Input() errorText = "";
	@Input() placeholder = "";
	@Input() min = moment().subtract(100, "y").format("DD/MM/YYYY");
	@Input() max = moment().add(100, "y").format("DD/MM/YYYY");

	@ViewChild("input", { static: true }) input: ElementRef;

	private listener = this.onClick.bind(this);

	value: any = "";
	showDatePicker = false;
	locale = "";
	currentDate: moment.Moment;
	dayLabels: IMyDayLabels;
	selectedDate: IMyDate;
	disabled = false;
	datePickerOptions: any;

	@HostBinding("class.error") @Input() error = false;
	@HostBinding("attr.tabIndex") tabIndex = 0;

	@HostListener("focus")
	onFocus() {
		this.input.nativeElement.focus();
	}

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: string) {
		this.value = value || "";
		if (value) {
			const momentDate = moment(value, "YYYY-MM-DD");
			this.value = momentDate.format("DD/MM/YYYY");
			this.selectedDate = {
				day: momentDate.get("date"),
				month: (momentDate.get("month") as number) + 1,
				year: momentDate.get("year"),
			};
		}
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.tabIndex = this.disabled ? -1 : 0;
		this.cdRef.markForCheck();
	}

	onBlur(value: string) {
		this.onTouch();
		this.value = value;
		const formattedDate = this.formatDate(value);
		if (!formattedDate || !formattedDate.isValid()) {
			// This ensures that an incorrect date will result in an 'incorrect date' error
			this.onModelChange(value);

			return;
		}
		this.onModelChange(formattedDate.format("YYYY-MM-DD"));
		this.value = formattedDate.format("DD/MM/YYYY");

		this.selectedDate = {
			day: formattedDate.get("date"),
			month: (formattedDate.get("month") as number) + 1,
			year: formattedDate.get("year"),
		};
	}

	focus() {
		this.eRef.nativeElement.focus();
	}

	private formatDate(value: string) {
		const date = moment(value, "DD/MM/YYYY");
		if (date.isValid) {
			return moment(value, "DD/MM/YYYY");
		}
	}

	toggleDatePicker() {
		if (this.disabled) {
			return;
		}

		this.showDatePicker = !this.showDatePicker;
		if (this.showDatePicker) {
			this.listenToClick();
		} else {
			this.removeClick();
		}
	}

	selectDate(event: any) {
		const value = moment(event.formatted, "YYYY-MM-DD");
		this.value = value.format("DD/MM/YYYY");
		this.onModelChange(value.format("YYYY-MM-DD"));
		this.toggleDatePicker();
		this.onTouch();
		this.selectedDate = {
			day: value.get("date"),
			month: (value.get("month") as number) + 1,
			year: value.get("year"),
		};
	}

	private setDatePickerOptions() {
		this.translator.get(["time"]).subscribe((translations) => {
			const todayBtn = translations.today;
			this.datePickerOptions = {
				inline: true,
				dateFormat: "yyyy-mm-dd",
				selectorHeight: "200px",
				selectorWidth: "180px",
				todayBtnTxt: todayBtn,
				hideMonthArrows: true,
				hideYearArrows: true,
				dayLabels: this.dayLabels,
				selectYearText: translations.selectYear,
				disableUntil: this.min,
				disableSince: this.max,
			};
		});
	}

	private listenToClick() {
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("click", this.listener);
		});
	}

	private removeClick() {
		window.removeEventListener("click", this.listener);
	}

	private onClick(event: MouseEvent) {
		event.preventDefault();

		if (this.eRef.nativeElement.contains(event.target)) {
			return;
		}

		this.ngZone.run(() => {
			this.showDatePicker = false;
			this.cdRef.markForCheck();
		});

		this.removeClick();
	}

	ngOnInit() {
		this.locale = this.translator.currentLang;
		moment.locale(this.locale);
		this.currentDate = moment();
		this.translator.get("time").subscribe((time) => {
			this.dayLabels = {
				su: time.su,
				mo: time.mo,
				tu: time.tu,
				we: time.we,
				th: time.th,
				fr: time.fr,
				sa: time.sa,
			};
		});
		this.setDatePickerOptions();
	}
}
